import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link, useNavigate } from 'react-router-dom';
import { LocalizationProvider, useLocalization } from './contexts/LocalizationContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LandingPage from './components/LandingPage';
import AuthForms from './components/AuthForms';
import Navigation from './components/Navigation';
import ModulesScreen from './components/ModulesScreen';
import DragonLairScreen from './components/DragonLairScreen';
import DragonSelection from './components/DragonSelection';
import AdminScreen from './components/AdminScreen';
import ProfileScreen from './components/ProfileScreen';
import CardScreen from './components/CardScreen';
import ResultScreen from './components/ResultScreen';
import AboutPage from './components/AboutPage';
import CookieBanner from './components/CookieBanner';
import ErrorPage from './components/ErrorPage';
import GeneratorsScreen from './components/GeneratorsScreen';
import ModuleReviewScreen from './components/ModuleReviewScreen';
import WelcomeModal from './components/WelcomeModal';
import Footer from './components/Footer';
import api from './services/api';
import './App.css';

const AppContent = () => {
  const { loading: localizationLoading, language, changeLanguage, t } = useLocalization();
  const { loading: authLoading, isAuthenticated, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [starCount, setStarCount] = useState(0);
  const [currentPlayer, setCurrentPlayer] = useState(null); // Currently selected player
  const [showLanding, setShowLanding] = useState(true); // Show landing page first
  const [showAuth, setShowAuth] = useState(false); // Show auth forms
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Custom player selection handler that persists to localStorage
  const handlePlayerSelect = (player) => {
    setCurrentPlayer(player);
    if (player) {
      localStorage.setItem('lastSelectedPlayerId', player.id.toString());
      console.log(`Saved player ${player.id} (${player.first_name}) to localStorage`);
      
      // Check if welcome modal should be shown
      if (!player.welcome_shown) {
        setShowWelcomeModal(true);
      }
    } else {
      localStorage.removeItem('lastSelectedPlayerId');
    }
  };

  // Load saved player on app initialization
  const loadSavedPlayer = (availablePlayers) => {
    const savedPlayerId = localStorage.getItem('lastSelectedPlayerId');
    if (savedPlayerId && availablePlayers && availablePlayers.length > 0) {
      const savedPlayer = availablePlayers.find(p => p.id.toString() === savedPlayerId);
      if (savedPlayer) {
        console.log(`Restored player ${savedPlayer.id} (${savedPlayer.first_name}) from localStorage`);
        setCurrentPlayer(savedPlayer);
        
        // Check if welcome modal should be shown for restored player
        if (!savedPlayer.welcome_shown) {
          setShowWelcomeModal(true);
        }
        
        return true; // Indicate that a saved player was found and restored
      }
    }
    return false; // No saved player found or restored
  };

  // Clear player persistence on logout
  const handleLogout = () => {
    localStorage.removeItem('lastSelectedPlayerId');
    setCurrentPlayer(null);
    setStarCount(0);
  };

  // Clear saved player data when user logs out (detected by user becoming null)
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      // User has logged out, clear player data
      localStorage.removeItem('lastSelectedPlayerId');
      setCurrentPlayer(null);
      setStarCount(0);
    }
  }, [isAuthenticated, authLoading]);

  // Load star count when currentPlayer changes
  useEffect(() => {
    if (currentPlayer && isAuthenticated()) {
      const loadStarCount = async () => {
        try {
          const token = localStorage.getItem('authToken');
          const response = await api.get(`/api/modules?player_id=${currentPlayer.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.data.total_stars !== undefined) {
            setStarCount(response.data.total_stars);
            console.log(`Loaded star count for player ${currentPlayer.first_name}: ${response.data.total_stars}`);
          }
        } catch (error) {
          console.error('Failed to load star count:', error);
        }
      };
      
      loadStarCount();
    }
  }, [currentPlayer, isAuthenticated]);
  
  // Determine current section from URL
  const getCurrentSection = () => {
    const path = location.pathname;
    if (path.startsWith('/dragon-lair')) return 'dragonLair';
    if (path.startsWith('/profile')) return 'profile';
    if (path.startsWith('/admin')) return 'admin';
    if (path.startsWith('/card') || path.startsWith('/result')) return 'card';
    return 'modules';
  };
  
  // Load current player when authenticated (only runs once per authentication)
  useEffect(() => {
    if (isAuthenticated() && !currentPlayer) {
      setShowLanding(false);
      setShowAuth(false);
      
      // Load dashboard data and initialize player selection
      const loadPlayerData = async () => {
        try {
          const response = await api.get('/api/dashboard');
          if (response.data.players && response.data.players.length > 0) {
            // Try to load saved player first, fallback to first player
            const savedPlayerLoaded = loadSavedPlayer(response.data.players);
            if (!savedPlayerLoaded) {
              console.log('No saved player found, using first player as default');
              const firstPlayer = response.data.players[0];
              setCurrentPlayer(firstPlayer);
              
              // Check if welcome modal should be shown for first player
              if (firstPlayer && !firstPlayer.welcome_shown) {
                setShowWelcomeModal(true);
              }
            }
          }
        } catch (error) {
          console.error('Failed to load player data:', error);
        }
      };
      
      loadPlayerData();
    } else if (isAuthenticated() && currentPlayer) {
      // User is authenticated and has a player selected - just update UI states
      setShowLanding(false);
      setShowAuth(false);
      console.log('User authenticated with existing player:', currentPlayer.first_name);
    }
  }, [isAuthenticated, currentPlayer, loadSavedPlayer]);

  // Handle player change from Navigation component
  const handlePlayerChange = (player) => {
    setCurrentPlayer(player);
  };

  // Function to refresh star count (can be called after earning stars)
  const refreshStarCount = async () => {
    if (isAuthenticated() && currentPlayer) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await api.get(`/api/modules?player_id=${currentPlayer.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.total_stars !== undefined) {
          setStarCount(response.data.total_stars);
        }
      } catch (error) {
        console.error('Failed to refresh star count:', error);
      }
    }
  };

  // Handle welcome modal close
  const handleWelcomeModalClose = () => {
    setShowWelcomeModal(false);
  };

  if (localizationLoading || authLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        {/* While translations themselves are loading, t() returns the key - fall back */}
        <p>{t('common.loading') === 'common.loading' ? 'Loading...' : t('common.loading')}</p>
      </div>
    );
  }

  const currentSection = getCurrentSection();
  const showNavigation = !showLanding && !showAuth && isAuthenticated();

  // Protected route component
  const ProtectedRoute = ({ children }) => {
    if (!isAuthenticated()) {
      return <Navigate to="/auth" replace />;
    }
    return children;
  };

  // Show main app with navigation and routes
  return (
    <>
      <CookieBanner />
      
      {/* Welcome Modal */}
      {showWelcomeModal && currentPlayer && (
        <WelcomeModal
          currentPlayer={currentPlayer}
          onClose={handleWelcomeModalClose}
        />
      )}
      
      <Routes>
        {/* About page - always accessible with appropriate navbar */}
        <Route path="/about" element={
          <>
            {isAuthenticated() ? (
              <Navigation 
                currentSection="about" 
                starCount={starCount}
                currentPlayer={currentPlayer}
              />
            ) : (
              <header className="wrapper bg-soft-primary">
                <nav className="navbar navbar-expand-lg center-nav transparent navbar-light">
                  <div className="container flex-lg-row flex-nowrap align-items-center">
                    <div className="navbar-brand w-100">
                      <Link to="/" className="text-decoration-none">
                        <h2 className="mb-0" style={{ fontFamily: 'Thicccboi, sans-serif', color: '#2563eb', fontWeight: '700', fontSize: '1.25rem' }}>Poligon</h2>
                      </Link>
                    </div>
                    <div className="navbar-other w-100 d-flex ms-auto">
                      <ul className="navbar-nav flex-row align-items-center ms-auto">
                        <li className="nav-item d-none d-md-block">
                          <Link 
                            to="/about" 
                            className="nav-link text-decoration-none text-uppercase fw-medium"
                            style={{ fontFamily: 'Thicccboi, sans-serif', fontSize: '0.875rem', letterSpacing: '0.05em', color: '#374151' }}
                          >
                            {t('navigation.about')}
                          </Link>
                        </li>
                        <li className="nav-item dropdown language-select text-uppercase">
                          <button 
                            className="nav-link dropdown-item dropdown-toggle border-0 bg-transparent"  
                            data-bs-toggle="dropdown" 
                            aria-haspopup="true" 
                            aria-expanded="false"
                          >
                            {language ? language.toUpperCase() : 'EN'}
                          </button>
                          <ul className="dropdown-menu">
                            <li className="nav-item">
                              <button 
                                className="dropdown-item border-0 bg-transparent w-100 text-start" 
                                onClick={() => changeLanguage('en')}
                              >
                                EN
                              </button>
                            </li>
                            <li className="nav-item">
                              <button
                                className="dropdown-item border-0 bg-transparent w-100 text-start"
                                onClick={() => changeLanguage('hr')}
                              >
                                HR
                              </button>
                            </li>
                            <li className="nav-item">
                              <button
                                className="dropdown-item border-0 bg-transparent w-100 text-start"
                                onClick={() => changeLanguage('nl')}
                              >
                                NL (BE)
                              </button>
                            </li>
                          </ul>
                        </li>
                        <li className="nav-item d-none d-md-block">
                          <Link
                            to="/auth"
                            className="btn btn-sm btn-primary rounded-pill"
                          >
                            {t('landing.getStarted')}
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </nav>
              </header>
            )}
            <AboutPage />
          </>
        } />
        
        {/* Landing and Auth routes */}
        <Route path="/" element={
          isAuthenticated() ? (
            <>
              <Navigation
                currentSection="landing"
                starCount={starCount}
                currentPlayer={currentPlayer}
              />
              <LandingPage
                onGetStarted={() => {
                  // Already logged in, go to play
                }}
                hideNavbar={true}
              />
            </>
          ) : (
            <LandingPage
              onGetStarted={() => {
                navigate('/auth');
              }}
            />
          )
        } />
        
        <Route path="/auth" element={
          isAuthenticated() ? <Navigate to="/profile" replace /> : <AuthForms />
        } />
        
        {/* Generators - accessible without login */}
        <Route path="/gen" element={<GeneratorsScreen />} />

        {/* Protected routes */}
        <Route path="/play" element={
          <ProtectedRoute>
            <>
              <Navigation 
                currentSection={getCurrentSection()} 
                starCount={starCount}
                currentPlayer={currentPlayer}
              />
              <ModulesScreen currentPlayer={currentPlayer} />
            </>
          </ProtectedRoute>
        } />
        
        <Route path="/dragon-selection" element={
          <ProtectedRoute>
            <>
              <Navigation 
                currentSection={getCurrentSection()} 
                starCount={starCount}
                currentPlayer={currentPlayer}
              />
              <DragonSelection currentPlayer={currentPlayer} />
            </>
          </ProtectedRoute>
        } />
        
        <Route path="/dragon-lair" element={
          <ProtectedRoute>
            <>
              <Navigation 
                currentSection={getCurrentSection()} 
                starCount={starCount}
                currentPlayer={currentPlayer}
              />
              <DragonLairScreen starCount={starCount} currentPlayer={currentPlayer} />
            </>
          </ProtectedRoute>
        } />
        
        <Route path="/profile" element={
          <ProtectedRoute>
            <>
              <Navigation 
                currentSection={getCurrentSection()} 
                starCount={starCount}
                currentPlayer={currentPlayer}
              />
              <ProfileScreen 
                currentPlayer={currentPlayer} 
                onPlayerSelect={handlePlayerSelect}
                loadSavedPlayer={loadSavedPlayer}
              />
            </>
          </ProtectedRoute>
        } />
        
        <Route path="/admin" element={
          <ProtectedRoute>
            {user?.role === 'admin' ? (
              <>
                <Navigation 
                  currentSection={getCurrentSection()} 
                  starCount={starCount}
                  currentPlayer={currentPlayer}
                />
                <AdminScreen />
              </>
            ) : (
              <Navigate to="/play" replace />
            )}
          </ProtectedRoute>
        } />
        
        {/* Card system routes */}
        <Route path="/play/:moduleName" element={
          <ProtectedRoute>
            <>
              <Navigation 
                currentSection={getCurrentSection()} 
                starCount={starCount}
                currentPlayer={currentPlayer}
              />
              <CardScreen currentPlayer={currentPlayer} />
            </>
          </ProtectedRoute>
        } />
        
        <Route path="/play/:moduleName/:cardId" element={
          <ProtectedRoute>
            <>
              <Navigation 
                currentSection={getCurrentSection()} 
                starCount={starCount}
                currentPlayer={currentPlayer}
              />
              <CardScreen currentPlayer={currentPlayer} />
            </>
          </ProtectedRoute>
        } />
        
        <Route path="/result" element={
          <ProtectedRoute>
            <>
              <Navigation 
                currentSection={getCurrentSection()} 
                starCount={starCount}
                currentPlayer={currentPlayer}
              />
              <ResultScreen refreshStarCount={refreshStarCount} currentPlayer={currentPlayer} />
            </>
          </ProtectedRoute>
        } />
        
        {/* Module Review route */}
        <Route path="/module-review/:moduleId" element={
          <ProtectedRoute>
            <>
              <Navigation 
                starCount={starCount} 
                currentPlayer={currentPlayer}
                onPlayerChange={handlePlayerChange}
              />
              <ModuleReviewScreen currentPlayer={currentPlayer} />
            </>
          </ProtectedRoute>
        } />
        
        {/* Error pages */}
        <Route path="/404" element={<ErrorPage type="404" />} />
        <Route path="/500" element={<ErrorPage type="500" />} />
        
        {/* Fallback route */}
        <Route path="*" element={<ErrorPage type="404" />} />
      </Routes>
    </>
  );
};

const App = () => {
  return (
    <LocalizationProvider>
      <AuthProvider>
        <Router basename="/app">
          <div className="App">
            <AppContent />
            <Footer />
          </div>
        </Router>
      </AuthProvider>
    </LocalizationProvider>
  );
};

export default App;